/**
 * The output contract shared by every command.
 *
 * One place decides how a set of results becomes terminal output and a process
 * exit code, so future commands stay consistent:
 *   - `--json` → a single structured object on stdout (machine-readable).
 *   - default → a one-line status per check on stdout; on failure, the
 *     AI-oriented fix instructions go to stderr (so piping stdout stays clean).
 *   - if any check failed → fail with `OperationFailed` (exit 1 via the runtime
 *     marker); `runMain` won't double-report because the error is marked reported.
 *
 * @module
 */
import { Console, Effect } from "effect"
import { OperationFailed } from "./errors"
import type { ValidationReport } from "./report"

export interface EmitOptions {
  readonly json: boolean
}

const RULE = "─".repeat(60)

/** Per-check entry in the `--json` payload. */
export interface JsonCheck {
  readonly check: string
  readonly ok: boolean
  readonly summary: string
  readonly aiText: string | null
  readonly detail: unknown
}

/** The `--json` payload — pure, so the machine-readable shape is unit-testable. */
export interface JsonPayload {
  readonly ok: boolean
  readonly checks: ReadonlyArray<JsonCheck>
}

/** Build the structured `--json` payload from a set of reports. */
export const toJsonPayload = (reports: ReadonlyArray<ValidationReport>): JsonPayload => ({
  ok: reports.every((r) => r.ok),
  checks: reports.map((r) => ({
    check: r.check,
    ok: r.ok,
    summary: r.summary,
    aiText: r.aiText,
    detail: r.detail,
  })),
})

/** Render reports per the output contract, then fail if any check did not pass. */
export const emitReports = (
  reports: ReadonlyArray<ValidationReport>,
  options: EmitOptions,
): Effect.Effect<void, OperationFailed> =>
  Effect.gen(function* () {
    const failed = reports.filter((r) => !r.ok)
    const ok = failed.length === 0

    if (options.json) {
      yield* Console.log(JSON.stringify(toJsonPayload(reports), null, 2))
    } else {
      for (const r of reports) {
        yield* Console.log(`${r.ok ? "✓" : "✗"} ${r.check} — ${r.summary}`)
      }
      // Fix instructions to stderr, so stdout stays parseable.
      for (const r of failed) {
        if (r.aiText !== null) {
          yield* Console.error(`\n${RULE}\nhow to fix: ${r.check}\n${RULE}`)
          yield* Console.error(r.aiText)
        }
      }
    }

    if (!ok) {
      return yield* new OperationFailed({ failed: failed.map((r) => r.check) })
    }
  })
